import React, { useState, useEffect, useMemo, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { withStyles } from '@material-ui/core/styles';
import {
  Paper,
  Typography,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  Badge,
  Divider
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import RefreshIcon from '@material-ui/icons/Refresh';
import MenuIcon from '@material-ui/icons/Menu';
import SortIcon from '@material-ui/icons/Sort';
import CloseIcon from '@material-ui/icons/Close';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import ViewsManager from 'components/explore/views/ViewsManager';
import ViewCard from 'components/explore/views/ViewCard';
import View from 'components/explore/views/View';
import SaveViewDialog from 'components/explore/views/SaveViewDialog';
import {
  fetchExploreViews,
  requestExploreSelectedViewIdChange,
  setExploreSelectedViewIsOpen,
  updateExploreViewsOrder,
  sortExploreViews,
  filterExploreViews,
  setExploreLocation,
  setExploreMapType,
  setIsSideBarOpen,
} from 'store/actions';
import { Constants } from 'utilities/constants';
import { viewMatchesFilter } from 'store/explore/views';
import { LoadingOverlayWrapper } from 'components/layout';
import { Resizable } from 're-resizable';
import { sendClientLog } from 'services/logService';
import { areEqual } from 'utilities';
import Dialog from "@material-ui/core/Dialog";

const menuStyles = { horizontal: 'right', vertical: 'top'};
const INFO = 'Info';
const OPENED_EXPLORE_VIEWS_PAGE_LOG = 'Explore Views - User opened Explore Views page';
const OPENED_VIEWS_MANAGER_LOG = 'Explore Views - User opened Views Manager from button';
const OPENED_CREATE_NEW_VIEW_LOG = 'Explore Views - User opened Views Manager from Create New View button';
const REFRESHED_VIEWS_LOG = 'Explore Views - User refreshed Views from ViewsPanel button';
const OPENED_SORT_MENU_LOG = 'Explore Views - User opened Sort and Filter menu from ViewsPanel button';
const CLOSED_SORT_MENU_LOG = 'Explore Views - User closed Sort and Filter menu from ViewsPanel button';

// filter icon is absent in current version of @material-ui/icons
const FilterIcon = function({ classes }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      opacity="0.8"
      fill="currentColor"
      className={`bi bi-funnel-fill ${classes.sortingIndicator}`}
      viewBox="0 0 16 16">
      <path
        d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5v-2z"/>
    </svg>
  );
}

const SortMenu = React.memo(function({
  addObjectMenuAnchorEl,
  closeSortMenu,
  updateExploreViewsOrder,
  sortExploreViews,
  filterExploreViews,
  sortObject,
  classes,
}) {
  const [isDisabledClearBtn, setIsDisabledClearBtn] = useState(true);

  useEffect(() => {
    const sortedField = sortObject.find(field => field.order);
    const filterField = sortObject.find(field => field.filter);

    if (sortedField) {
      // if asc/desc sorting was applied need to update views order and turn on Clear button
      setIsDisabledClearBtn(false);
      updateExploreViewsOrder({
        sortOrder: sortedField.order,
        type: sortedField.type
      });
    } else {
      if (filterField) {
        setIsDisabledClearBtn(false);
        // need to update order if sorting was deleted but filter applied
        updateExploreViewsOrder({ type: 'DEFAULT' });
        return;
      }
      setIsDisabledClearBtn(true);
      updateExploreViewsOrder({ type: 'DEFAULT' });
    }
  }, [sortObject]);

  const sortMenuItems = useMemo(() => {
    return sortObject.filter(item => item.hasOwnProperty('order')).map(item => {
      return <MenuItem
        key={item.name}
        className={classes.menuItem}
        onClick={() => sortExploreViews(item.type)}
      >
        <Typography variant="inherit">{item.name}</Typography>
        {item.order === 'asc'
          ? <ArrowUpwardIcon
              fontSize="small"
              className={classes.sortingIndicator}
            />
          : item.order === 'desc'
            ? <ArrowDownwardIcon
                fontSize="small"
                className={classes.sortingIndicator}
              />
            : <div style={{ width: 28 }}/> // Spacer to ensure constant menu width even without icon
        }
      </MenuItem>
    });
  }, [sortObject])

  const filterMenuItems = useMemo(() => {
    return sortObject.filter(item => item.hasOwnProperty('filter')).map(item => {
      return <MenuItem
        key={item.name}
        className={classes.menuItem}
        onClick={() => filterExploreViews(item.type)}
      >
        <Typography variant="inherit">{item.name}</Typography>
        {item.filter ? <FilterIcon classes={classes} /> : null}
      </MenuItem>
    });
  }, [sortObject])

  return (
    <Menu
      anchorEl={addObjectMenuAnchorEl}
      open={Boolean(addObjectMenuAnchorEl)}
      onClose={closeSortMenu}
      anchorOrigin={menuStyles}
    >
      {sortObject.length 
        ? <div>
            <Typography variant='subtitle2' className={classes.menuHeading} style={{ marginTop: 0 }}>Sort</Typography>
            <Divider className={classes.headingDivider}/>
            {sortMenuItems}
            <Typography variant='subtitle2' className={classes.menuHeading}>Filter</Typography>
            <Divider className={classes.headingDivider}/>
            {filterMenuItems}
            <Divider className={classes.divider}/>
            <MenuItem
              className={classes.menuItem}
              disabled={isDisabledClearBtn}
              onClick={() => sortExploreViews('DEFAULT')}
            >
              <Typography variant="inherit">{'Clear'}</Typography>
            </MenuItem>
          </div>
        : null
      }
    </Menu>
  )
}, areEqual);

const ViewsPanel = ({
  classes,
  user,
  fetchExploreViews,
  views,
  viewsLoadingState,
  selectedViewId,
  selectedView,
  selectedViewIsOpen,
  setExploreSelectedViewIsOpen,
  requestExploreSelectedViewIdChange,
  saveViewDialogParameters,
  updateExploreViewsOrder,
  sortExploreViews,
  filterExploreViews,
  sortObject,
  setExploreLocation,
  setExploreMapType,
  isMobile,
  isSideBarOpen,
  setIsSideBarOpen
}) => {
  const [viewsManagerState, setViewsManagerState] = useState({ open: false });
  const [addObjectMenuAnchorEl, setAddObjectMenuAnchorEl] = useState(null);

  useEffect(() => {
    sendClientLog(INFO, OPENED_EXPLORE_VIEWS_PAGE_LOG);
  }, []);

  useEffect(() => {
    if (!views) {
      fetchExploreViews();
    }
  }, [views]);

  const previousId = useRef(selectedView?.id); // Seed with the already-selected view so a remount (eg leaving Explore and returning) doesn't re-apply the view's saved position over where the user left the map
  useEffect(() => { // handle View-level actions here- map location, map type, etc
    if (!selectedView) return;
    if (previousId.current === selectedView.id) { // Same view already selected (remount) - keep the user's current map position instead of snapping back to the view's saved home
      return;
    }
    if (previousId.current === 'temp') { // No need to run on initial view save (aka temp id -> db id change)
      previousId.current = selectedView.id;
      return;
    }
    if (selectedView.useSavedPosition) {
      const center = selectedView.latitude != null && selectedView.longitude != null ? { lat: selectedView.latitude, lng: selectedView.longitude } : null;
      setTimeout(() => setExploreLocation({ zoom: selectedView.zoomLevel, center, bounds: selectedView.bounds }), 10);
    }
    if (selectedView.mapViewType) {
      setExploreMapType(selectedView.mapViewType);
    }
    // cancelExploreDataFetch();
    previousId.current = selectedView.id;
  }, [selectedView?.id]);

  const sortingIndicator = useMemo(() => {
    const sortedField = sortObject.find(field => field.order || field.filter);
    if (sortedField?.name === 'Last Updated' && sortedField?.order === 'desc') return false; // Last Updated desc is set as the default sort, don't show indicator in this case
    return !!sortedField;
  }, [sortObject]);

  const activeFilterType = useMemo(() => {
    return sortObject.find(field => field.filter)?.type ?? null;
  }, [sortObject]);

  const handleViewCardClick = (id) => {
    if (id !== selectedViewId) {
      requestExploreSelectedViewIdChange(id);
    } else {
      setExploreSelectedViewIsOpen(true);
    }
  }

  const handleManageViewsClick = () => {
    setViewsManagerState({ open: true });
    sendClientLog(INFO, OPENED_VIEWS_MANAGER_LOG);
  }

  const handleCreateNewViewClick = () => {
    setViewsManagerState({ open: true, shouldCollapseGrid: true });
    sendClientLog(INFO, OPENED_CREATE_NEW_VIEW_LOG);
  }

  const handleRefreshViewsClick = () => {
    fetchExploreViews();
    sendClientLog(INFO, REFRESHED_VIEWS_LOG);
  }

  const openSortMenu = (e) => {
    setAddObjectMenuAnchorEl(e.currentTarget);
    sendClientLog(INFO, OPENED_SORT_MENU_LOG);
  }
  const closeSortMenu = () => {
    setAddObjectMenuAnchorEl(null);
    sendClientLog(INFO, CLOSED_SORT_MENU_LOG);
  }

  const foundView = views?.find(view => view.id === selectedViewId);
  const canSave = selectedView?.id !== 'system' && (selectedView?.id === 'temp' || user.roles.administrator || foundView?.createdByUserId === user.id);
  const { isLoading, message } = viewsLoadingState;
  const hasTempOrSystemView = selectedViewId === 'temp' || selectedViewId === 'system';
  return (
    isMobile ? <>
       <Dialog
        keepMounted
        fullScreen
        open={isSideBarOpen}
      >
        <Paper className={classes.mobilePanelContainer}>
          {selectedViewId != null && selectedViewIsOpen
            ? <View name={selectedView?.id === selectedViewId ? selectedView?.name : foundView?.name ?? null} />
            : <>
                <div className={classes.header}>
                  <div className={classes.headerLeft}>
                    <Tooltip title='Manage Views'>
                      <IconButton
                        onClick={handleManageViewsClick}
                        size='small'
                        color='inherit'
                        classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}
                        style={{ margin: '0 4px'}}
                      >
                        <MenuIcon />
                      </IconButton>
                    </Tooltip>
                    <Typography variant='subtitle2' className={classes.headerText} color='inherit'>{'Views'}</Typography>
                  </div>
                  <div className={classes.headerRight}>
                  <Tooltip title='Create New View'>
                    <span>
                      <IconButton
                      onClick={handleCreateNewViewClick}
                      disabled={isLoading}
                      size='small' color='inherit' classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}>
                        <AddCircleIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                    <Tooltip title='Refresh Views'>
                      <IconButton
                        onClick={handleRefreshViewsClick}
                        disabled={isLoading}
                        size='small' color='inherit' classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Sort and Filter">
                      <Badge
                        invisible={!sortingIndicator}
                        classes={{ badge: classes.sortBadge }}
                        color="secondary"
                        variant="dot"
                        overlap='rectangular'
                      >
                        <IconButton
                          onClick={openSortMenu}
                          color='inherit'
                          classes={{
                            root: classes.iconButton,
                            disabled: classes.headerIconDisabled
                          }}
                        >
                          <SortIcon />
                        </IconButton>
                      </Badge>
                    </Tooltip>
                    <SortMenu
                      addObjectMenuAnchorEl={addObjectMenuAnchorEl}
                      closeSortMenu={closeSortMenu}
                      updateExploreViewsOrder={updateExploreViewsOrder}
                      sortExploreViews={sortExploreViews}
                      filterExploreViews={filterExploreViews}
                      sortObject={sortObject}
                      classes={classes}
                    />
                  <Tooltip title='Close Panel'>
                    <span>
                      <IconButton
                      onClick={() => setIsSideBarOpen(false)}
                      size='small' color='inherit' classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}>
                        <CloseIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  </div>
                </div>
                <LoadingOverlayWrapper
                  className={classes.overlay}
                  active={isLoading}
                  message={message}
                >
                  <div className={classes.listContainer}>
                    {views?.length || hasTempOrSystemView
                      ? <>
                          {views?.filter(view => viewMatchesFilter(view, activeFilterType, user))?.map(view =>
                            <ViewCard key={view.id} view={view} onClick={handleViewCardClick}/>
                          )}
                          {hasTempOrSystemView &&
                            <ViewCard view={selectedView} onClick={handleViewCardClick}/>
                          }
                        </>
                      : <div className={classes.noObjectsDiv}><Typography className={classes.noObjectsText} variant='body2'>No views found</Typography></div>}
                  </div>
                </LoadingOverlayWrapper>
              </>
          }
        </Paper>
      <ViewsManager
        modalOpen={viewsManagerState?.open}
        shouldCollapseGrid={viewsManagerState?.shouldCollapseGrid}
        onClose={() => setViewsManagerState({ open: false })}
      />
      {saveViewDialogParameters?.dialogIsOpen && ( // not using the open parameter to open the dialog, there's an issue with it after it closes, and click anywhere in the UI, it always triggers the onClose action
        <SaveViewDialog canSave={canSave} />
      )}
      </Dialog>
    </> :
    <>
      <Resizable
        className={classes.resizableContainer}
        // ref={gridResizable}
        minWidth='250' // Use vh/vw or px only, not %. Limitation of re-resizable- % of parent does not calculate correctly in a vertical flex layout
        maxWidth='50vw'
        defaultSize={{ width: ('440px') }}
        enable={{ top: false, right: true, bottom: false, left: false, topRight: false, bottomRight: false, bottomLeft: false, topLeft: false }}
        // onResize={updateResizableSizeDebounced}
      >
    <Paper className={classes.panelContainer}>
          {selectedViewId != null && selectedViewIsOpen
            ? <View name={selectedView?.id === selectedViewId ? selectedView?.name : foundView?.name ?? null} />
            : <>
                <div className={classes.header}>
                  <div className={classes.headerLeft}>
                    <Tooltip title='Open Views Manager'>
                      <IconButton
                        onClick={handleManageViewsClick}
                        size='small'
                        color='inherit'
                        classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}
                        style={{ margin: '0 4px'}}
                      >
                        <MenuIcon />
                      </IconButton>
                    </Tooltip>
                    <Typography variant='subtitle2' className={classes.headerText} color='inherit'>{'Views'}</Typography>
                  </div>
                  <div className={classes.headerRight}>
                  <Tooltip title='Create New View'>
                    <span>
                      <IconButton
                      onClick={handleCreateNewViewClick}
                      disabled={isLoading}
                      size='small' color='inherit' classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}>
                        <AddCircleIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                    <Tooltip title='Refresh Views'>
                      <IconButton
                        onClick={handleRefreshViewsClick}
                        disabled={isLoading}
                        size='small' color='inherit' classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Sort and Filter">
                      <Badge
                        invisible={!sortingIndicator}
                        classes={{ badge: classes.sortBadge }}
                        color="secondary"
                        variant="dot"
                        overlap='rectangular'
                      >
                        <IconButton
                          onClick={openSortMenu}
                          color='inherit'
                          classes={{
                            root: classes.iconButton,
                            disabled: classes.headerIconDisabled
                          }}
                        >
                          <SortIcon />
                        </IconButton>
                      </Badge>
                    </Tooltip>
                    <SortMenu
                      addObjectMenuAnchorEl={addObjectMenuAnchorEl}
                      closeSortMenu={closeSortMenu}
                      updateExploreViewsOrder={updateExploreViewsOrder}
                      sortExploreViews={sortExploreViews}
                      filterExploreViews={filterExploreViews}
                      sortObject={sortObject}
                      classes={classes}
                    />
                  </div>
                </div>
                <LoadingOverlayWrapper
                  className={classes.overlay}
                  active={isLoading}
                  message={message}
                >
                  <div className={classes.listContainer}>
                    {views?.length || hasTempOrSystemView
                      ? <>
                          {views?.filter(view => viewMatchesFilter(view, activeFilterType, user))?.map(view =>
                            <ViewCard key={view.id} view={view} onClick={handleViewCardClick}/>
                          )}
                          {hasTempOrSystemView &&
                            <ViewCard view={selectedView} onClick={handleViewCardClick}/>
                          }
                        </>
                      : <div className={classes.noObjectsDiv}><Typography className={classes.noObjectsText} variant='body2'>No views found</Typography></div>}
                  </div>
                </LoadingOverlayWrapper>
              </>
          }
        </Paper>
      <ViewsManager
        modalOpen={viewsManagerState?.open}
        shouldCollapseGrid={viewsManagerState?.shouldCollapseGrid}
        onClose={() => setViewsManagerState({ open: false })}
      />
      {saveViewDialogParameters?.dialogIsOpen && ( // not using the open parameter to open the dialog, there's an issue with it after it closes, and click anywhere in the UI, it always triggers the onClose action
        <SaveViewDialog canSave={canSave} />
      )}
      </Resizable>
    </>
  )
}

const styles = theme => ({
  resizableContainer: {
    zIndex: 1
  },
  panelContainer: {
    flexShrink: 0,
    backgroundColor: '#f2f2f2',
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  mobilePanelContainer: { 
    zIndex: 1,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'unset'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#3e4350',
    color: '#fff',
    marginBottom: 8
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    margin: '8px 0',
  },
  headerText: {
    fontSize: '1.2rem',
    marginRight: 8
  },
  headerRight: {
    //color: '#fff'
    marginRight: 4,
    display: 'flex'
  },
  iconButton: {
    padding: '5px',
  },
  headerIconDisabled: {
    color: 'rgba(255, 255, 255, 0.2)!important'
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflow: 'auto'
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1
  },
  noObjectsDiv: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 42,
    margin: '4px 8px'
  },
  noObjectsText: {
    fontStyle: 'italic',
    color: '#a7a9ac',
    fontSize: '.9rem'
  },
  overlay: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0
  },
  sortingIndicator: {
    marginLeft: '0.5rem'
  },
  sortBadge: {
    marginTop: 6,
    marginRight: 6
  },
  menuItem: {
    paddingLeft: 24
  },
  menuHeading: {
    margin: '8px 8px 4px'
  },
  headingDivider: {
    marginBottom: 8
  },
  divider: {
    margin: '8px 0'
  }
});

const mapStateToProps = state => {
  const { exploreViews } =  state;

  return {
    user: state.appState.user,
    views: exploreViews.views,
    viewsLoadingState: exploreViews.viewsLoadingState,
    selectedViewId: exploreViews.selectedViewId,
    selectedView: exploreViews.selectedView,
    selectedViewIsOpen: exploreViews.selectedViewIsOpen,
    saveViewDialogParameters: exploreViews.saveViewDialogParameters,
    sortObject: exploreViews.sortObject,
    isMobile: state.appState.isMobile,
    isSideBarOpen: state.exploreViews.isSideBarOpen,
  }
};

const mapDispatchToProps = {
  fetchExploreViews,
  requestExploreSelectedViewIdChange,
  setExploreSelectedViewIsOpen,
  updateExploreViewsOrder,
  sortExploreViews,
  filterExploreViews,
  setExploreLocation,
  setExploreMapType,
  setIsSideBarOpen,
};

export default compose(
  withRouter,
  withStyles(styles),
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ViewsPanel);
